import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { X, Clock, Sparkles, CalendarDays, Plus, Loader2, Save, Pencil, RefreshCw, Check, TriangleAlert } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';
import { labourService } from '../../../services/labourService';
import AddLabourFunctionModal from './AddLabourFunctionModal';
import SearchSelect from '../../../components/SearchSelect';
import OptimiseScheduleModal from './OptimiseScheduleModal';

// =============================================================================
// Variable Labour — SHIFT PATTERN PLANNER
//
// Shared by Distribution (MOD_4_MOVE) and Manufacturing (MOD_3_MAKE): the
// caller passes its module, and the plan is stored per version + module +
// FUNCTION + site. A function is the team being planned (Picking, Packing,
// Extrusion, …) chosen from the selector at the top, or added inline.
//
// The site is ALLOWED a number of shifts per month (edited on this page). For a
// given month the manager designs the working pattern:
//   1. Choose the shift LENGTH (hours) — the day divides into equal blocks.
//   2. On a Mon–Sun × blocks grid, tick which blocks are worked each day.
//   3. Assign the shifts. Two ways, the manager's choice:
//        • By timeslot — a repeating weekly template (which shift covers each
//          block, Mon–Sun). An optional ALTERNATING flag rotates the crews one
//          timeslot forward each rotation period so they cycle day→evening→night.
//        • Per day — opens the FULL MONTH CALENDAR (real dates) so the schedule
//          can be hand-crafted date by date (public holidays, one-off overtime,
//          irregular weeks). Times & heads still come from the weekly template;
//          the calendar just picks which dates each shift runs — giving EXACT
//          monthly hours (by date) instead of a ~4.33-week estimate.
//
// Persisted by labourController: bud_labour_plan (month header),
// bud_labour_slots (weekly template) and bud_labour_calendar (per-day).
//
// Pattern/hours only — NO pay. Rates/premiums come from People and are joined
// downstream, gated to MOD_8_PEOPLE holders.
// =============================================================================

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_OPTIONS = [6, 8, 12];
const AVG_WEEKS_PER_MONTH = 4.33;

const pad = (h) => String(h).padStart(2, '0');
// Blocks are cut from `startHour`, not from midnight, so a 06:00 day start with
// 8h blocks gives 06–14, 14–22, 22–06. The last block of the day can therefore
// run past midnight; it still belongs to the date it STARTED on, which is what
// the backend assumes too.
const blocksFor = (hours, startHour = 0) => {
  const count = Math.floor(24 / hours);
  return Array.from({ length: count }, (_, idx) => {
    const raw = (Number(startHour) || 0) + idx * hours;
    const start = raw % 24;
    const end = (start + hours) % 24;
    return {
      idx,
      start,
      wraps: start + hours > 24,
      label: `${pad(start)}:00–${pad(end === 0 ? 24 : end)}:00`,
    };
  });
};
const emptyWorked = (n) => Array.from({ length: n }, () => Array(7).fill(false));

// Paint a weekly template onto a month's real dates: dayBlocks[date] = the
// crew working each timeslot that day. Module scope because both the organiser
// (switching a month to per-day) and the page-level optimiser (applying a
// proposed roster in per-day mode) need it, and two copies of a calendar
// mapping is how a plan starts disagreeing with itself.
const materialiseFor = (m, cal) => {
  const ds = {};
  const bl = blocksFor(m.blockHours, m.dayStartHour);
  for (let date = 1; date <= cal.daysInMonth; date++) {
    const weekday = (cal.firstWeekday + date - 1) % 7;
    ds[date] = bl.map(({ idx: b }) => ((m.worked[b]?.[weekday] && m.assign[b]?.shiftId) ? m.assign[b].shiftId : 0));
  }
  return ds;
};

// Calendar for a financial period, on the VERSION'S real year. The FY start
// month decides which calendar month a period maps to; `financialYear` is the
// year period 1 falls in, so a February start rolls the tail of the year over.
const calendarFor = (periodIdx, fyStartMonth, financialYear) => {
  const offset = ((Number(fyStartMonth) || 1) - 1) + periodIdx;
  const year = (Number(financialYear) || new Date().getFullYear()) + Math.floor(offset / 12);
  const monthIndex = ((offset % 12) + 12) % 12;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const firstWeekday = (new Date(year, monthIndex, 1).getDay() + 6) % 7; // Mon=0..Sun=6
  return { monthIndex, year, daysInMonth, firstWeekday };
};
const isoDate = (year, monthIndex, day) =>
  `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
// Short timeslot label for tight calendar cells, e.g. "08:00–16:00" → "08–16".
const shortSlot = (label) => label.replace(/:00/g, '');

const SHIFT_STYLES = [
  { name: 'Shift 1', dot: 'bg-amber-500', cell: 'bg-amber-100 dark:bg-amber-500/25 border-amber-300 dark:border-amber-500/40', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400' },
  { name: 'Shift 2', dot: 'bg-indigo-500', cell: 'bg-indigo-100 dark:bg-indigo-500/25 border-indigo-300 dark:border-indigo-500/40', chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400' },
  { name: 'Shift 3', dot: 'bg-emerald-500', cell: 'bg-emerald-100 dark:bg-emerald-500/25 border-emerald-300 dark:border-emerald-500/40', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' },
  { name: 'Shift 4', dot: 'bg-violet-500', cell: 'bg-violet-100 dark:bg-violet-500/25 border-violet-300 dark:border-violet-500/40', chip: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400' },
  { name: 'Shift 5', dot: 'bg-rose-500', cell: 'bg-rose-100 dark:bg-rose-500/25 border-rose-300 dark:border-rose-500/40', chip: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400' },
];
const shiftStyle = (n) => SHIFT_STYLES[(n - 1) % SHIFT_STYLES.length];

// ---- Weekly-template read helpers -------------------------------------------
const isWorked = (m, b, d) => !!m.worked?.[b]?.[d];
const effShift = (m, b, d) => (m.worked?.[b]?.[d] ? (m.assign?.[b]?.shiftId || null) : null);

// Headcount is NOT stored on the plan. It is however many positions People >
// Structures has on the crew that works the block, read back from the server
// via getShifts. `headsOf` maps a shift number to that count.
const blockHeads = (m, b, headsOf) => headsOf(m.assign?.[b]?.shiftId);

// How many times each weekday (Mon..Sun) falls in a financial period's real
// calendar month. THIS IS THE FIX FOR THE 4.33-WEEK ESTIMATE: a month is not
// 4.33 identical weeks. March 2026 has five Sundays; February has four of
// everything. At double time a fifth Sunday is the difference between a roster
// that lands and one that blows the budget, and the costing (calc_labour_hours,
// which walks real dates) always knew that — only this page did not, so the
// screen and the calc disagreed by a few percent on every plan.
const dowCountsFor = (periodIdx, fyStartMonth, financialYear) => {
  const { monthIndex, year, daysInMonth, firstWeekday } = calendarFor(periodIdx, fyStartMonth, financialYear);
  void monthIndex; void year;
  const counts = Array(7).fill(0);
  for (let d = 0; d < daysInMonth; d++) counts[(firstWeekday + d) % 7] += 1;
  return counts;
};

// Scheduled hours for one month.
//
// `dowCounts` is the real shape of that month (see dowCountsFor). It is
// REQUIRED for the by-timeslot mode: without it the answer is a 4.33-week
// approximation that will not match what the costing produces on save, and a
// planner comparing the two has no way to tell a rounding artefact from a
// mistake. Per-day mode was always exact — it counts real dates already.
const monthHours = (m, headsOf, dowCounts) => {
  // Per day -> exact by date: over dates, over timeslots worked, block-hours x heads.
  if (m.assignMode === 'perday') {
    let total = 0;
    Object.values(m.dayBlocks || {}).forEach((arr) => {
      arr.forEach((sid, b) => { if (sid > 0) total += m.blockHours * headsOf(sid); });
    });
    return Math.round(total);
  }
  // By timeslot -> the weekly template laid over the month's actual weekdays.
  let total = 0;
  blocksFor(m.blockHours, m.dayStartHour).forEach(({ idx: b }) => {
    const heads = blockHeads(m, b, headsOf);
    if (!heads) return;
    for (let d = 0; d < 7; d++) {
      if (!isWorked(m, b, d)) continue;
      // No dowCounts supplied is a caller bug, not a mode. Falling back to the
      // old average keeps the page rendering rather than throwing mid-paint,
      // but it is the one path that can still disagree with the calc.
      total += m.blockHours * heads * (dowCounts ? dowCounts[d] : AVG_WEEKS_PER_MONTH);
    }
  });
  return Math.round(total);
};
const distinctShifts = (m) => {
  const ids = new Set();
  if (m.assignMode === 'perday') Object.values(m.dayBlocks || {}).forEach((arr) => arr.forEach((s) => { if (s > 0) ids.add(s); }));
  else blocksFor(m.blockHours, m.dayStartHour).forEach(({ idx: b }) => { for (let d = 0; d < 7; d++) { const s = effShift(m, b, d); if (s) ids.add(s); } });
  return [...ids].sort((a, b) => a - b);
};
const slotsPerWeek = (m) => {
  let n = 0;
  blocksFor(m.blockHours, m.dayStartHour).forEach(({ idx: b }) => { for (let d = 0; d < 7; d++) if (isWorked(m, b, d)) n++; });
  return n;
};
const workingDays = (m) => Object.values(m.dayBlocks || {}).filter((arr) => arr.some((v) => v > 0)).length;
const fmtH = (n) => `${Math.round(Number(n || 0)).toLocaleString()}h`;
// No currency symbol: the base currency is per version, and this page has no
// business deciding what it is. The number is what matters here.
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

// Pull whatever the server actually said out of an axios error. The labour
// endpoints return `error` on a 500 and `message` on a 400, so reading only
// one of them turns a real Postgres message into "Request failed with status
// code 500" — which is how a broken calc ends up looking like a dead button.
const errText = (e, fallback) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

// The Postgres detail the labour endpoints attach to a 500, formatted as one
// block a user can copy straight into a bug report. SQLSTATE plus `where` (the
// PL/pgSQL context stack) is usually enough to identify the failing statement
// inside a stored proc without any database access.
const pgReport = (pg, context) => [
  `Mantle — Variable Labour`,
  context && `Action: ${context}`,
  `Time: ${new Date().toISOString()}`,
  pg?.code && `SQLSTATE: ${pg.code}`,
  pg?.message && `Message: ${pg.message}`,
  pg?.detail && `Detail: ${pg.detail}`,
  pg?.hint && `Hint: ${pg.hint}`,
  pg?.schema && `Schema: ${pg.schema}`,
  pg?.table && `Table: ${pg.table}`,
  pg?.column && `Column: ${pg.column}`,
  pg?.constraint && `Constraint: ${pg.constraint}`,
  pg?.routine && `Routine: ${pg.routine}`,
  pg?.where && `Context:\n${pg.where}`,
].filter(Boolean).join('\n');

// Shown instead of a bare red line when the failure came from the database.
const PgErrorPanel = ({ pg, context, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const text = pgReport(pg, context);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the text is on screen and selectable */ }
  };
  return (
    <div className="rounded-lg border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="m-0 text-sm font-semibold text-rose-800 dark:text-rose-200">
            Database error{pg?.code ? ` · SQLSTATE ${pg.code}` : ''}
          </p>
          <p className="m-0 mt-0.5 text-xs text-rose-700 dark:text-rose-300 break-words">{pg?.message}</p>
          {pg?.hint && <p className="m-0 mt-1 text-xs text-rose-600 dark:text-rose-400">Hint: {pg.hint}</p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button type="button" onClick={copy}
            className="px-2 py-1 text-[11px] font-semibold rounded-md border border-rose-300 dark:border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-500/20">
            {copied ? 'Copied' : 'Copy for support'}
          </button>
          <button type="button" onClick={onDismiss} title="Dismiss"
            className="p-1 rounded text-rose-400 hover:text-rose-700 dark:hover:text-rose-200">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer text-[11px] font-medium text-rose-600 dark:text-rose-400">Full detail</summary>
        <pre className="mt-1.5 max-h-56 overflow-auto rounded bg-white/70 dark:bg-slate-900/60 p-2 text-[10px] leading-relaxed text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">{text}</pre>
      </details>
    </div>
  );
};

// The two halves of a save. `pct` is where the bar sits WHILE that step runs —
// each boundary is a real response, so the bar only moves when something
// actually finished.
const STAGES = {
  plan: { key: 'plan', label: 'Saving shift plan…', pct: 20 },
  calc: { key: 'calc', label: 'Calculating hours worked, overtime and cost…', pct: 70 },
  done: { key: 'done', label: 'Done', pct: 100 },
};

// One headline number beside the function selector.
const Stat = ({ label, value, hint, accent = false }) => (
  <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 px-2.5 py-1 leading-tight" title={hint}>
    <div className="text-[9px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-xs font-mono font-semibold ${accent
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-slate-800 dark:text-slate-100'}`}>{value}</div>
  </div>
);

// The three cost splits and their total, as one group so they read as parts of
// a whole rather than four unrelated numbers. Colours match the mix bar.
const COST_SPLITS = [
  { key: 'base_cost', label: 'Base', dot: 'bg-slate-400', text: 'text-slate-700 dark:text-slate-200',
    hint: 'Ordinary time — hours at a multiplier of 1.0' },
  { key: 'overtime_cost', label: 'Overtime', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400',
    hint: 'Every premium hour: overtime, Saturday, Sunday and public holiday. The FULL cost of those hours, not just the uplift.' },
  { key: 'allowance_cost', label: 'Allowance', dot: 'bg-indigo-500', text: 'text-indigo-600 dark:text-indigo-400',
    hint: 'Shift and night allowances' },
];

const CostSplit = ({ data }) => {
  const total = Number(data?.total_cost || 0);
  const pct = (v) => (total > 0 ? (Number(v || 0) / total) * 100 : 0);
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 px-2.5 py-1 leading-tight">
      <div className="flex items-stretch gap-3">
        {COST_SPLITS.map((s) => (
          <div key={s.key} title={s.hint}>
            <div className="flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide text-slate-400">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
            </div>
            <div className={`text-xs font-mono font-semibold ${s.text}`}>{fmtMoney(data?.[s.key])}</div>
          </div>
        ))}
        <div className="border-l border-slate-300 dark:border-slate-600 pl-3" title="Base + overtime + allowance, for the year">
          <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Total cost</div>
          <div className="text-xs font-mono font-bold text-slate-900 dark:text-white">{fmtMoney(total)}</div>
        </div>
      </div>
      {/* The mix, at a glance. Hidden at zero so an uncosted function is not a
          misleading empty bar. */}
      {total > 0 && (
        <div className="mt-1 flex h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          {COST_SPLITS.map((s) => (
            <span key={s.key} className={s.dot} style={{ width: `${pct(data?.[s.key])}%` }} />
          ))}
        </div>
      )}
    </div>
  );
};
const cloneMonth = (m) => ({
  ...m,
  worked: m.worked.map((r) => [...r]),
  assign: JSON.parse(JSON.stringify(m.assign)),
  dayBlocks: JSON.parse(JSON.stringify(m.dayBlocks || {})),
});

// A function with no saved plan yet starts empty rather than pre-populated —
// nothing is budgeted until someone designs the pattern.
const blankMonth = () => ({
  shiftsAllowed: 1, blockHours: 8, dayStartHour: 0, worked: emptyWorked(3), assign: {},
  dayBlocks: {}, assignMode: 'timeslot', alternating: false, rotationWeeks: 2,
});

// ---- API <-> page state -----------------------------------------------------
const monthsFromApi = ({ plan = [], slots = [], calendar = [] }) => {
  const headers = new Map(plan.map((p) => [Number(p.financial_period), p]));
  const slotsBy = {};
  slots.forEach((s) => {
    const p = Number(s.financial_period);
    if (!slotsBy[p]) slotsBy[p] = [];
    slotsBy[p].push(s);
  });
  const calBy = {};
  calendar.forEach((c) => {
    const p = Number(c.financial_period);
    if (!calBy[p]) calBy[p] = [];
    calBy[p].push(c);
  });

  return PERIODS.map((p) => {
    const h = headers.get(p);
    if (!h) return blankMonth();

    const blockHours = Number(h.block_hours) || 8;
    const n = Math.floor(24 / blockHours);
    const worked = emptyWorked(n);
    const assign = {};
    (slotsBy[p] || []).forEach((s) => {
      const b = Number(s.block_idx);
      if (b < 0 || b >= n) return;                 // stale row from a different block length
      worked[b] = (s.days_worked || []).map(Boolean);
      assign[b] = { shiftId: s.shift_no == null ? null : Number(s.shift_no), heads: Number(s.heads) || 0 };
    });

    const dayBlocks = {};
    (calBy[p] || []).forEach((c) => {
      const b = Number(c.block_idx);
      if (b < 0 || b >= n) return;
      const day = Number(String(c.work_date).slice(8, 10));
      if (!dayBlocks[day]) dayBlocks[day] = Array(n).fill(0);
      dayBlocks[day][b] = Number(c.shift_no) || 0;
    });

    return {
      shiftsAllowed: Number(h.shifts_allowed) || 1,
      blockHours,
      dayStartHour: Number(h.day_start_hour) || 0,
      worked,
      assign,
      dayBlocks,
      assignMode: h.assign_mode === 'perday' ? 'perday' : 'timeslot',
      alternating: !!h.alternating,
      rotationWeeks: Number(h.rotation_weeks) || 2,
    };
  });
};

// The whole year is sent on every save: the organiser's "Apply to remaining
// months" rewrites arbitrarily many months, and the backend replaces a month's
// child rows wholesale, so a partial payload would be the harder thing to
// reason about.
const monthsToApi = (months, fyStartMonth, financialYear, headsOf, crewCount) => months.map((m, idx) => {
  const blocks = blocksFor(m.blockHours, m.dayStartHour);
  const cal = calendarFor(idx, fyStartMonth, financialYear);

  const slots = blocks.map(({ idx: b }) => {
    const shiftId = m.assign?.[b]?.shiftId || null;
    return {
      block_idx: b,
      days_worked: Array.from({ length: 7 }, (_, d) => !!m.worked?.[b]?.[d]),
      shift_no: shiftId,
      // Derived from the roster, so bud_labour_slots.heads is a snapshot of
      // Structures rather than an independently-typed second opinion.
      heads: headsOf(shiftId),
    };
  });

  const calendar = [];
  Object.entries(m.dayBlocks || {}).forEach(([date, arr]) => {
    const day = Number(date);
    if (!(day >= 1 && day <= cal.daysInMonth)) return;
    (arr || []).forEach((sid, b) => {
      if (sid > 0 && b < blocks.length) {
        calendar.push({ work_date: isoDate(cal.year, cal.monthIndex, day), block_idx: b, shift_no: sid });
      }
    });
  });

  return {
    financial_period: idx + 1,
    // No longer a free-standing setting: you may run as many shifts as you
    // have crews, so the crew list IS the limit.
    shifts_allowed: Math.max(1, crewCount),
    block_hours: m.blockHours,
    assign_mode: m.assignMode,
    alternating: m.alternating,
    rotation_weeks: m.rotationWeeks,
    day_start_hour: m.dayStartHour || 0,
    slots,
    calendar,
  };
});

// Rotation cycles: shifts move forward one timeslot each period.
const rotationCycles = (m) => {
  const workedBlocks = blocksFor(m.blockHours, m.dayStartHour).filter((b) => m.worked[b.idx]?.some(Boolean) && m.assign[b.idx]?.shiftId);
  const n = workedBlocks.length;
  if (n <= 1) return [];
  const baseShifts = workedBlocks.map((b) => m.assign[b.idx].shiftId);
  return Array.from({ length: n }, (_, c) =>
    workedBlocks.map((b, i) => ({ label: b.label, shiftId: baseShifts[(i - c + n) % n] })));
};

// ---- Organiser modal --------------------------------------------------------
const ShiftOrganiser = ({ month, monthIdx, fyStartMonth, financialYear, shiftLabel,
  crews = [], headsOf, crewName, moduleCode, functionCode, onApply, onApplyForward, onClose }) => {
  const [draft, setDraft] = useState(() => cloneMonth(month));
  // Optimiser. The planner is PROMPTED rather than asked cold: the panel shows
  // the volume budgeted for this month and the hours-per-unit the current plan
  // implies, then asks for the target rate. Only the user knows that rate —
  // it is the metric most plants are actually run against — but almost nobody
  // can name the raw hours, so volume x rate is the sum the page does for them.
  const [optOpen, setOptOpen] = useState(false);
  const [optCtx, setOptCtx] = useState(null);        // volumes + current + implied
  const [optLoading, setOptLoading] = useState(false);
  const [optBusy, setOptBusy] = useState(false);
  const [optError, setOptError] = useState('');
  const [optSource, setOptSource] = useState('production');   // production | dispatch
  const [optBasis, setOptBasis] = useState('pallets');        // pallets | units
  const [optWorkedRate, setOptWorkedRate] = useState('');
  const [optPaidRate, setOptPaidRate] = useState('');
  const [optResult, setOptResult] = useState(null);
  const [brush, setBrush] = useState(1); // active calendar brush: shift number, or 'off'
  const blocks = blocksFor(draft.blockHours, draft.dayStartHour);
  const monthName = periodLabel(monthIdx + 1, fyStartMonth);
  // The brush palette is the crews that actually exist, not 1..shiftsAllowed.
  // A shift you cannot staff is not a shift you can plan.
  const shiftOptions = crews.map((c) => Number(c.shift_no));
  const perday = draft.assignMode === 'perday';
  const cal = calendarFor(monthIdx, fyStartMonth, financialYear);
  // The month's real weekday shape — what makes the footer total agree with
  // what calc_labour_hours will write on save.
  const dowCounts = dowCountsFor(monthIdx, fyStartMonth, financialYear);

  // Materialise the calendar from the weekly template: each date gets, per block,
  // the shift that covers that block on that weekday (0 = off). Per day starts
  // from the weekly plan, then hand-edit by timeslot.
  const materialise = (d) => materialiseFor(d, cal);

  const setHours = (h) => {
    const n = Math.floor(24 / h);
    setDraft((d) => ({ ...d, blockHours: h, worked: emptyWorked(n), assign: {}, dayBlocks: {} }));
  };

  const setMode = (mode) => setDraft((d) => {
    if (mode === d.assignMode) return d;
    if (mode === 'perday') {
      const dayBlocks = (d.dayBlocks && Object.keys(d.dayBlocks).length) ? d.dayBlocks : materialise(d);
      return { ...d, assignMode: 'perday', dayBlocks, alternating: false };
    }
    return { ...d, assignMode: 'timeslot' };
  });

  const toggleCell = (b, day) => setDraft((d) => { const worked = d.worked.map((r) => [...r]); worked[b][day] = !worked[b][day]; return { ...d, worked }; });
  const toggleRow = (b) => setDraft((d) => { const worked = d.worked.map((r) => [...r]); const allOn = worked[b].every(Boolean); worked[b] = worked[b].map(() => !allOn); return { ...d, worked }; });
  const assignShift = (b, shiftId) => setDraft((d) => ({ ...d, assign: { ...d.assign, [b]: { ...(d.assign[b] || { heads: 1 }), shiftId } } }));

  // Per-day calendar: paint the active brush onto ONE timeslot of a date.
  const paintBlock = (date, b) => setDraft((d) => {
    const dayBlocks = { ...d.dayBlocks };
    const arr = [...(dayBlocks[date] || blocksFor(d.blockHours, d.dayStartHour).map(() => 0))];
    arr[b] = brush === 'off' ? 0 : (arr[b] === brush ? 0 : brush);
    dayBlocks[date] = arr;
    return { ...d, dayBlocks };
  });
  const resetCalendar = () => setDraft((d) => ({ ...d, dayBlocks: materialise(d) }));

  // ---- Optimise ------------------------------------------------------------
  // Three steps, in this order, because the last one is unanswerable without
  // the first two: show the volume, show the rate the plan runs at today, then
  // ask for the target rate. Volume x rate is the hours the search aims at.
  const openOptimiser = async () => {
    if (optOpen) { setOptOpen(false); return; }
    setOptOpen(true);
    if (optCtx) return;
    setOptLoading(true); setOptError('');
    try {
      const c = await labourService.getOptimiseContext(moduleCode, functionCode);
      setOptCtx(c);
      setOptSource(c.defaultSource || 'production');
      // Seed the target with what the plan already runs at. A planner who
      // cannot name their standard cold can almost always say whether last
      // year's number was good, and this makes that the starting point.
      const implied = c.implied?.[monthIdx]?.[c.defaultSource || 'production'];
      if (implied?.perPallet) setOptWorkedRate(String(implied.perPallet));
    } catch (e) {
      setOptError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally { setOptLoading(false); }
  };

  const runOptimiser = async () => {
    setOptBusy(true); setOptError(''); setOptResult(null);
    try {
      const r = await labourService.optimise(moduleCode, functionCode, {
        financial_period: monthIdx + 1,
        source: optSource,
        basis: optBasis,
        worked_hours_per_unit: Number(optWorkedRate) || undefined,
        paid_hours_per_unit: Number(optPaidRate) || undefined,
      });
      if (!r.success) setOptError(r.message || 'No roster could be proposed.');
      else setOptResult(r);
    } catch (e) {
      setOptError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally { setOptBusy(false); }
  };

  // Applying a pattern REPLACES the worked grid and the crew assignment, and
  // drops back to the weekly template — the search reasons about a repeating
  // week, so leaving a hand-built calendar in place would show a plan that is
  // not the one proposed. Nothing is saved until the planner presses Apply.
  const applyPattern = (p) => {
    setDraft((d) => {
      const n = Math.floor(24 / p.blockHours);
      const worked = emptyWorked(n);
      const assign = {};
      p.blocks.forEach((b) => {
        worked[b.blockIdx] = [...b.worked];
        assign[b.blockIdx] = { ...(d.assign?.[b.blockIdx] || {}), shiftId: b.shiftId };
      });
      return { ...d, blockHours: p.blockHours, worked, assign, dayBlocks: {}, assignMode: 'timeslot' };
    });
    setOptResult(null);
    setOptOpen(false);
  };


  const cycles = draft.alternating ? rotationCycles(draft) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-center justify-between">
          <div>
            <h3 className="m-0 text-base font-semibold text-slate-900 dark:text-slate-100">Shift plan · {monthName}</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-400">Design the working pattern for this month.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* 1 — length, allowance, mode, alternating */}
          <section>
            <h4 className="m-0 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">1 · Shift setup</h4>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 dark:text-slate-400">Hours</span>
                {HOUR_OPTIONS.map((h) => (
                  <button key={h} type="button" onClick={() => setHours(h)}
                    className={`px-2.5 py-1.5 text-xs rounded border transition-colors ${draft.blockHours === h ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>{h}h</button>
                ))}
                <span className="text-[11px] text-slate-400">{blocks.length} blocks</span>
              </div>

              {/* Day start — rotates the blocks off midnight. Changing it does
                  NOT reshuffle who works what: block 0 is still the first
                  block of the day, it just starts later. */}
              <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
                title="The clock hour the working day starts at. The last block of the day may run past midnight; a shift belongs to the date it started on.">
                Day starts
                <select value={draft.dayStartHour || 0}
                  onChange={(e) => setDraft((d) => ({ ...d, dayStartHour: Number(e.target.value) || 0 }))}
                  className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400">
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{pad(h)}:00</option>
                  ))}
                </select>
              </label>

              {/* Assignment mode */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500 dark:text-slate-400">Assign</span>
                {[['timeslot', 'By timeslot'], ['perday', 'Per day']].map(([mode, lbl]) => (
                  <button key={mode} type="button" onClick={() => setMode(mode)}
                    className={`px-2.5 py-1.5 text-xs rounded border transition-colors ${draft.assignMode === mode ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>{lbl}</button>
                ))}
              </div>

              {/* Read-only: the crews you have ARE the shifts you may run. Add
                  or remove them on the Crews bar behind this dialog. */}
              <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
                title="Set by the crews on this function — add or remove crews to change it.">
                Shifts available
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{crews.length}</span>
              </span>
            </div>

            {/* Alternating (timeslot mode only) */}
            {!perday && (
              <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-violet-200 dark:border-violet-500/30 bg-violet-50/60 dark:bg-violet-500/5 px-3 py-2">
                <label className="flex items-center gap-2 text-xs font-medium text-violet-700 dark:text-violet-300">
                  <input type="checkbox" checked={draft.alternating} onChange={() => setDraft((d) => ({ ...d, alternating: !d.alternating }))} />
                  Alternating rotation
                </label>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">crews rotate one timeslot forward every</span>
                <input type="number" min="1" value={draft.rotationWeeks} disabled={!draft.alternating}
                  onChange={(e) => setDraft((d) => ({ ...d, rotationWeeks: Math.max(1, Number(e.target.value) || 1) }))}
                  className="w-14 text-right text-xs rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-40" />
                <span className="text-[11px] text-slate-500 dark:text-slate-400">weeks</span>
              </div>
            )}
          </section>

          {/* 2 — week grid (By timeslot) OR month calendar (Per day) */}
          {!perday ? (
            <section>
              <h4 className="m-0 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">2 · Which blocks are worked each day</h4>
              <div className="overflow-x-auto">
                <table className="border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-slate-500 dark:text-slate-400 w-40">Timeslot</th>
                      {DAYS.map((d) => <th key={d} className="px-1 py-1.5 text-center font-semibold text-slate-500 dark:text-slate-400 w-12">{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {blocks.map((b) => (
                      <tr key={b.idx}>
                        <td className="px-2 py-1">
                          <button type="button" onClick={() => toggleRow(b.idx)} title="Toggle whole week"
                            className="font-mono text-[11px] text-slate-600 dark:text-slate-300 hover:underline">{b.label}</button>
                        </td>
                        {DAYS.map((d, di) => {
                          const on = isWorked(draft, b.idx, di);
                          const sid = effShift(draft, b.idx, di);
                          const st = sid ? shiftStyle(sid) : null;
                          return (
                            <td key={d} className="px-0.5 py-0.5">
                              <button type="button" onClick={() => toggleCell(b.idx, di)}
                                className={`w-full h-8 rounded border transition-colors ${on ? (st ? st.cell : 'bg-slate-200 dark:bg-slate-600 border-slate-300 dark:border-slate-500') : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}>
                                {on && <span className="text-[10px] font-bold text-slate-600 dark:text-slate-200">{sid ? `S${sid}` : '✓'}</span>}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="m-0 mt-1.5 text-[11px] text-slate-400">Click a cell to work that block; click the timeslot label to toggle the whole week.</p>
            </section>
          ) : (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="m-0 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <CalendarDays className="w-3.5 h-3.5" /> 2 · {monthName} {cal.year} — paint shifts onto dates
                </h4>
                <button type="button" onClick={resetCalendar} className="text-[11px] text-slate-500 dark:text-slate-400 hover:underline">Reset to weekly pattern</button>
              </div>

              {/* Brush palette */}
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                <span className="text-[11px] text-slate-400">Brush:</span>
                {shiftOptions.map((sid) => { const st = shiftStyle(sid); const on = brush === sid; return (
                  <button key={sid} type="button" onClick={() => setBrush(sid)}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-colors ${on ? `${st.chip} border-transparent ring-1 ring-slate-400` : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                    <span className={`w-2 h-2 rounded-full ${st.dot}`} />{shiftLabel(sid)}
                  </button>
                ); })}
                <button type="button" onClick={() => setBrush('off')}
                  className={`px-2 py-1 text-[11px] rounded border transition-colors ${brush === 'off' ? 'bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>Off</button>
                <span className="text-[11px] text-slate-400 ml-1">then click dates</span>
              </div>

              {/* Month calendar — each day shows its timeslots and the shift on each */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="grid grid-cols-7 bg-slate-100 dark:bg-slate-700/60">
                  {DAYS.map((d) => <div key={d} className="px-1 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                  {Array.from({ length: cal.firstWeekday }).map((_, i) => <div key={`b${i}`} className="min-h-[4.6rem] border-t border-r border-slate-100 dark:border-slate-700/60 bg-slate-50/40 dark:bg-slate-800/30" />)}
                  {Array.from({ length: cal.daysInMonth }, (_, i) => i + 1).map((date) => {
                    const weekday = (cal.firstWeekday + date - 1) % 7;
                    const isWeekend = weekday >= 5;
                    const arr = draft.dayBlocks[date] || blocks.map(() => 0);
                    return (
                      <div key={date} className={`min-h-[4.6rem] border-t border-r border-slate-100 dark:border-slate-700/60 p-1 ${isWeekend ? 'bg-slate-50/60 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-800'}`}>
                        <div className="text-[10px] font-semibold text-slate-400 mb-0.5">{date}</div>
                        <div className="space-y-0.5">
                          {blocks.map((b) => {
                            const sid = arr[b.idx] || 0;
                            const st = sid ? shiftStyle(sid) : null;
                            return (
                              <button key={b.idx} type="button" onClick={() => paintBlock(date, b.idx)}
                                title={`${b.label} — ${sid ? shiftLabel(sid) : 'off'}`}
                                className={`w-full flex items-center justify-between gap-1 rounded px-1 h-4 border text-[8px] leading-none transition-colors ${
                                  st ? `${st.cell}` : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                                }`}>
                                <span className={`font-mono ${st ? 'text-slate-600 dark:text-slate-200' : 'text-slate-400'}`}>{shortSlot(b.label)}</span>
                                <span className="font-bold text-slate-600 dark:text-slate-200">{sid ? `S${sid}` : ''}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                Pick a shift brush, then click a <span className="font-medium">timeslot inside a day</span> to set which shift works it (Off clears). Times &amp; heads come from section 3; hours are exact by date.
              </p>
            </section>
          )}

          {/* 3 — shift catalogue: which shift owns each timeslot, and its heads */}
          <section>
            <h4 className="m-0 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              3 · {perday ? 'Each shift’s timeslot & headcount' : 'Which shift works each timeslot'}
            </h4>
            <div className="space-y-2">
              {blocks.map((b) => {
                let worked = perday; // in Per-day the calendar drives dates, so keep rows active
                for (let d = 0; d < 7 && !worked; d++) if (isWorked(draft, b.idx, d)) worked = true;
                const cur = draft.assign[b.idx] || { shiftId: null };
                const heads = headsOf(cur.shiftId);
                return (
                  <div key={b.idx} className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${worked ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800' : 'border-dashed border-slate-200 dark:border-slate-700 opacity-50'}`}>
                    <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300 w-28">{b.label}</span>
                    <div className="flex items-center gap-1">
                      {shiftOptions.map((sid) => {
                        const on = cur.shiftId === sid; const st = shiftStyle(sid);
                        return (
                          <button key={sid} type="button" disabled={!worked} onClick={() => assignShift(b.idx, sid)}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-colors disabled:opacity-40 ${on ? `${st.chip} border-transparent ring-1 ring-slate-400` : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                            <span className={`w-2 h-2 rounded-full ${st.dot}`} />{shiftLabel(sid)}
                          </button>
                        );
                      })}
                    </div>
                    {/* Heads is read-only — it is the crew's size in People >
                        Structures, fetched with the crew list. Nothing to type,
                        and nothing that can drift from the nominal roll. */}
                    <span className={`flex items-center gap-1.5 text-[11px] ml-auto ${worked ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 opacity-40'}`}
                      title={cur.shiftId
                        ? `${crewName(cur.shiftId)} has ${heads} position${heads === 1 ? '' : 's'} in People > Structures`
                        : 'Assign a crew to this timeslot to see its headcount'}>
                      Heads
                      <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                        {cur.shiftId ? heads : '—'}
                      </span>
                      {cur.shiftId && heads === 0 && (
                        <span className="text-amber-600 dark:text-amber-400">no one assigned</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Rotation preview */}
            {draft.alternating && cycles.length > 1 && (
              <div className="mt-3 rounded-lg border border-violet-200 dark:border-violet-500/30 bg-violet-50/50 dark:bg-violet-500/5 p-3">
                <p className="m-0 mb-2 text-[11px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">Rotation preview</p>
                <div className="space-y-1.5">
                  {cycles.map((cy, c) => (
                    <div key={c} className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 w-20">Wk {c * draft.rotationWeeks + 1}–{(c + 1) * draft.rotationWeeks}</span>
                      {cy.map((seg, i) => { const st = shiftStyle(seg.shiftId); return (
                        <span key={i} className="inline-flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
                          <span className="font-mono text-slate-400">{seg.label.split('–')[0]}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold ${st.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />S{seg.shiftId}</span>
                        </span>
                      ); })}
                    </div>
                  ))}
                </div>
                <p className="m-0 mt-2 text-[11px] text-slate-400">Each period every crew moves one timeslot forward, so day/evening/night rotate evenly.</p>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-3 py-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Estimated scheduled hours · {monthName}</span>
              <span className="text-sm font-bold font-mono text-slate-800 dark:text-slate-100">{fmtH(monthHours(draft, headsOf, dowCounts))}</span>
            </div>
          </section>
        </div>

        {optOpen && (
          <div className="px-5 py-4 border-t border-violet-200 dark:border-violet-500/30 bg-violet-50/60 dark:bg-violet-500/5">
            {optLoading && (
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Reading the volume plan&hellip;
              </div>
            )}

            {optCtx && (() => {
              const vol = optCtx.volumes?.[optSource]?.[monthIdx] || { units: 0, pallets: 0 };
              const qty = optBasis === 'pallets' ? vol.pallets : vol.units;
              const cur = optCtx.current?.[monthIdx] || { worked: 0, paid: 0 };
              const imp = optCtx.implied?.[monthIdx]?.[optSource] || {};
              const impRate = optBasis === 'pallets' ? imp.perPallet : imp.perUnit;
              const unitWord = optBasis === 'pallets' ? 'pallet' : 'unit';
              return (
                <>
                  {/* 1 — what production says is coming */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Expected volume &middot; {monthName}</span>
                      <span className="text-lg font-bold font-mono text-slate-900 dark:text-slate-100">
                        {Number(qty || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                      </span>
                      <span className="ml-1 text-slate-500 dark:text-slate-400">{unitWord}s</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Seg value={optSource} onChange={(v) => { setOptSource(v); setOptResult(null); }}
                        options={[['production', 'Production'], ['dispatch', 'Dispatch']]} />
                      <Seg value={optBasis} onChange={(v) => { setOptBasis(v); setOptResult(null); }}
                        options={[['pallets', 'Pallets'], ['units', 'Units']]} />
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="block">Plan today: <span className="font-mono font-semibold">{fmtH(cur.worked)}</span> worked &middot; <span className="font-mono font-semibold">{fmtH(cur.paid)}</span> paid</span>
                      {impRate != null && (
                        <span className="block">
                          which is <span className="font-mono font-semibold text-violet-700 dark:text-violet-300">{impRate}</span> worked hours per {unitWord}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 2 — the metric only the planner knows */}
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="text-xs">
                      <span className="block mb-1 font-semibold text-violet-800 dark:text-violet-300">Target worked hours / {unitWord}</span>
                      <input type="number" step="0.0001" min={0} value={optWorkedRate}
                        onChange={(e) => { setOptWorkedRate(e.target.value); setOptResult(null); }}
                        className="w-28 px-2 py-1.5 text-xs rounded-md border border-violet-300 dark:border-violet-500/40 bg-white dark:bg-slate-900 font-mono" />
                    </label>
                    <label className="text-xs">
                      <span className="block mb-1 font-semibold text-slate-500 dark:text-slate-400">Target paid hours / {unitWord} <span className="font-normal">(optional)</span></span>
                      <input type="number" step="0.0001" min={0} value={optPaidRate}
                        onChange={(e) => { setOptPaidRate(e.target.value); setOptResult(null); }}
                        className="w-28 px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 font-mono" />
                    </label>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 pb-1.5">
                      {Number(optWorkedRate) > 0 && (
                        <span className="block">&rarr; aiming at <span className="font-mono font-semibold">{fmtH(qty * Number(optWorkedRate))}</span> worked</span>
                      )}
                      {Number(optPaidRate) > 0 && (
                        <span className="block">&rarr; and <span className="font-mono font-semibold">{fmtH(qty * Number(optPaidRate))}</span> paid</span>
                      )}
                    </div>
                    <button type="button" onClick={runOptimiser} disabled={optBusy || (!Number(optWorkedRate) && !Number(optPaidRate))}
                      className="px-3 py-2 text-xs font-semibold rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40">
                      {optBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Find roster'}
                    </button>
                    <button type="button" onClick={() => { setOptOpen(false); setOptResult(null); }}
                      className="ml-auto p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-white dark:hover:bg-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </>
              );
            })()}

            {optError && (
              <p className="m-0 mt-3 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <TriangleAlert className="w-4 h-4 shrink-0 mt-px" />{optError}
              </p>
            )}

            {/* 3 — the roster */}
            {optResult?.ok && (
              <div className="mt-3 space-y-2">
                {optResult.unreachable && (
                  <p className="m-0 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="w-4 h-4 shrink-0 mt-px" />
                    These crews cannot reach {fmtH(optResult.target.workedHours)}. Working every timeslot, every
                    day, they deliver {fmtH(optResult.maxWorkedHours)}. That needs more people, not a different roster.
                  </p>
                )}

                <div className="rounded-lg border border-violet-200 dark:border-violet-500/30 bg-white dark:bg-slate-800 p-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="font-bold text-slate-800 dark:text-slate-100">
                      {optResult.best.blockHours}h shifts &middot; {optResult.best.crewsUsed} crew{optResult.best.crewsUsed === 1 ? '' : 's'}
                    </span>
                    <span><span className="text-slate-400">worked</span> <span className="font-mono font-semibold">{fmtH(optResult.best.workedHours)}</span>
                      {optResult.target.workedHours != null && <span className="text-slate-400"> / {fmtH(optResult.target.workedHours)}</span>}
                    </span>
                    <span><span className="text-slate-400">paid</span> <span className="font-mono font-semibold">{fmtH(optResult.best.paidHours)}</span>
                      {optResult.target.paidHours != null && <span className="text-slate-400"> / {fmtH(optResult.target.paidHours)}</span>}
                    </span>
                    <span className={optResult.best.premiumPct > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}
                      title="How much more the roster is paid than worked — Saturday, Sunday, public holidays and overtime.">
                      {optResult.best.premiumPct}% premium
                    </span>
                  </div>

                  <div className="mt-2 space-y-1">
                    {optResult.best.blocks.map((b) => {
                      const st = shiftStyle(b.shiftId);
                      const label = blocksFor(optResult.best.blockHours, draft.dayStartHour)[b.blockIdx]?.label || '';
                      return (
                        <div key={b.blockIdx} className="flex items-center gap-2 text-[11px]">
                          <span className="font-mono text-slate-400 w-24">{label}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold ${st.chip}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{crewName(b.shiftId)}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">{b.heads} heads</span>
                          <span className="flex gap-0.5 ml-1">
                            {DAYS.map((d, i) => (
                              <span key={d} title={d}
                                className={`w-5 text-center rounded ${b.worked[i]
                                  ? 'bg-violet-200 text-violet-800 dark:bg-violet-500/30 dark:text-violet-200'
                                  : 'bg-slate-100 text-slate-300 dark:bg-slate-700 dark:text-slate-600'}`}>
                                {d[0]}
                              </span>
                            ))}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <button type="button" onClick={() => applyPattern(optResult.best)}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-violet-600 hover:bg-violet-700">
                    <Check className="w-3.5 h-3.5" /> Use this roster
                  </button>
                  <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
                    Replaces the grid below. Nothing is saved until you press Apply.
                  </span>
                </div>

                {optResult.alternatives?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">Or:</span>
                    {optResult.alternatives.map((a, i) => (
                      <button key={i} type="button" onClick={() => applyPattern(a)}
                        className="px-2 py-1 text-[11px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
                        {a.blockHours}h &middot; {a.crewsUsed} crew &middot; {fmtH(a.workedHours)} worked &middot; {a.premiumPct}% prem
                      </button>
                    ))}
                  </div>
                )}

                <p className="m-0 text-[11px] text-slate-400 dark:text-slate-500">
                  Hours are counted on {monthName}&rsquo;s real dates
                  {optResult.holidays > 0 && `, including ${optResult.holidays} public holiday${optResult.holidays === 1 ? '' : 's'}`}
                  {' '}&mdash; not a 4.33-week average, because a fifth Sunday at double time is exactly what decides
                  whether a roster is affordable. Rosters that tie on hours are separated by
                  {optResult.rankedOnCost ? ' what they actually cost' : ' paid hours (no pay is captured yet)'};
                  no wage is shown here or sent to this screen.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <button type="button" onClick={openOptimiser}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-violet-300 dark:border-violet-500/40 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-500/20"
            title="Size the roster from the volume plan and your hours-per-unit target">
            <Sparkles className="w-3.5 h-3.5" /> Optimise
          </button>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => onApplyForward(draft)}
              className="px-3 py-2 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              title="Apply this plan to this month and every month after it">Apply to remaining months</button>
            <button type="button" onClick={() => onApply(draft)}
              className="px-4 py-2 text-xs font-semibold rounded-md text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white">Apply to {monthName}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Page -------------------------------------------------------------------
// `moduleCode` files the plan under the calling module; `title` lets each module
// use its own heading — the pattern/hours logic is identical.
const VariableLabour = ({
  moduleCode = 'MOD_4_MOVE',
  title = 'Warehousing · Variable Labour',
} = {}) => {
  const { fyStartMonth, selectedVersion, selectedSite } = useAppContext();

  const [functions, setFunctions] = useState([]);
  const [pickers, setPickers] = useState({ costCenters: [], glAccounts: [] });
  const [functionCode, setFunctionCode] = useState('');
  const [shifts, setShifts] = useState([]);          // [{ shift_no, shift_name }]
  const [shiftsDirty, setShiftsDirty] = useState(false);
  const [savingShifts, setSavingShifts] = useState(false);
  const [financialYear, setFinancialYear] = useState(null);
  const [months, setMonths] = useState(() => PERIODS.map(() => blankMonth()));
  const [organising, setOrganising] = useState(null);
  // The page-level optimiser: set the rules once, size all twelve months.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Which MONTHS changed, not just "something did" — only those are sent, and
  // the backend replaces exactly the periods it receives.
  const [dirty, setDirty] = useState(() => new Set());
  const [savedNote, setSavedNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editFn, setEditFn] = useState(null);   // the function being configured, or null to add
  // Costed totals per crew, straight off calc_labour_costs. Written by the calc
  // that runs on save, so this is the calculation's own output — not a second
  // estimate the page makes for itself.
  const [summary, setSummary] = useState(null);
  const [recalcing, setRecalcing] = useState(false);
  const [stage, setStage] = useState(null);     // which half of the save is running
  // Postgres detail from the last failure, kept separately from `error` so the
  // copyable escalation block survives a plain validation message.
  const [pgFault, setPgFault] = useState(null); // { pg, context }

  const ready = !!selectedVersion && !!selectedSite;

  // ---- Functions (the selector) --------------------------------------------
  const loadFunctions = useCallback(async (preferCode) => {
    if (!ready) { setFunctions([]); setFunctionCode(''); return; }
    setError('');
    try {
      const res = await labourService.getFunctions(moduleCode);
      if (res?.success) {
        const list = res.data.functions || [];
        setFunctions(list);
        setPickers({ costCenters: res.data.costCenters || [], glAccounts: res.data.glAccounts || [] });
        // Keep the current pick if it survived the reload, else fall to the first.
        setFunctionCode((cur) => {
          const wanted = preferCode || cur;
          return list.some((f) => f.function_code === wanted) ? wanted : (list[0]?.function_code || '');
        });
      } else {
        setError(res?.message || 'Failed to load functions.');
      }
    } catch (e) {
      setError(errText(e, 'Failed to load functions.'));
    }
  }, [ready, moduleCode]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);

  // ---- Plan for the selected function --------------------------------------
  const loadPlan = useCallback(async () => {
    if (!ready || !functionCode) { setMonths(PERIODS.map(() => blankMonth())); setShifts([]); return; }
    setLoading(true);
    setError('');
    try {
      const [planRes, shiftRes] = await Promise.all([
        labourService.getPlan(moduleCode, functionCode),
        labourService.getShifts(moduleCode, functionCode),
      ]);
      if (planRes?.success) {
        setFinancialYear(planRes.data.financialYear);
        setMonths(monthsFromApi(planRes.data));
        setDirty(new Set());
      } else {
        setError(planRes?.message || 'Failed to load the shift plan.');
      }
      setShifts(shiftRes?.success ? (shiftRes.data.shifts || []) : []);
      setShiftsDirty(false);
      // The costed numbers are a separate read: they come from calc_labour_costs,
      // which the last save wrote. A failure here must not blank the planner.
      try {
        const sum = await labourService.getSummary(moduleCode, functionCode);
        setSummary(sum?.success ? sum.data : null);
      } catch { setSummary(null); }
    } catch (e) {
      setError(errText(e, 'Failed to load the shift plan.'));
    } finally {
      setLoading(false);
    }
  }, [ready, moduleCode, functionCode]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  // ---- Crews (named shifts) -------------------------------------------------
  // shift_no is what the grid and calendar paint; the name is what the crew is
  // actually called, so "S2" reads as "Lions" everywhere there is room for it.
  // Crew size, straight from the roster (getShifts counts bud_structures). The
  // planner no longer holds its own copy, so the two can never disagree.
  const headsOf = useCallback((sid) => {
    if (!sid) return 0;
    return Number(shifts.find((x) => Number(x.shift_no) === Number(sid))?.headcount) || 0;
  }, [shifts]);

  const shiftLabel = useCallback((sid) => {
    const s = shifts.find((x) => Number(x.shift_no) === Number(sid));
    return s?.shift_name || `Shift ${sid}`;
  }, [shifts]);

  const addCrew = () => {
    const next = shifts.reduce((mx, s) => Math.max(mx, Number(s.shift_no)), 0) + 1;
    if (next > SHIFT_STYLES.length) return;
    setShifts((prev) => [...prev, { shift_no: next, shift_name: `Shift ${next}` }]);
    setShiftsDirty(true);
  };
  const renameCrew = (no, name) => {
    setShifts((prev) => prev.map((s) => (s.shift_no === no ? { ...s, shift_name: name } : s)));
    setShiftsDirty(true);
  };
  const removeCrew = async (no) => {
    setError('');
    try {
      await labourService.deleteShift(moduleCode, functionCode, no);
      setShifts((prev) => prev.filter((s) => s.shift_no !== no));
    } catch (e) {
      setError(errText(e, 'Could not remove the crew.'));
    }
  };
  const saveCrews = async () => {
    if (savingShifts || shifts.length === 0) return;
    setSavingShifts(true);
    setError('');
    try {
      const res = await labourService.saveShifts(moduleCode, functionCode,
        shifts.map((s) => ({ shift_no: s.shift_no, shift_name: (s.shift_name || '').trim() || `Shift ${s.shift_no}` })));
      if (res?.success) setShiftsDirty(false);
      else setError(res?.message || 'Could not save the crews.');
    } catch (e) {
      setError(errText(e, 'Could not save the crews.'));
    } finally {
      setSavingShifts(false);
    }
  };

  // What the next save will write — shown next to the button so a big Per-day
  // year does not just look like a frozen screen.
  const pending = useMemo(() => {
    const idxs = [...dirty].sort((a, b) => a - b);
    const rows = idxs.reduce((a, i) => {
      const m = months[i];
      if (!m) return a;
      return a + Object.values(m.dayBlocks || {}).reduce((n, arr) => n + arr.filter((s) => s > 0).length, 0);
    }, 0);
    return { months: idxs.length, rows };
  }, [dirty, months]);

  // Saving is two real round trips, not one long one, so the progress the user
  // sees is the progress that is actually happening:
  //   1. write the plan   (defer_calc — the server skips the calc)
  //   2. recalculate      (hours per shift, overtime bands, allowances, P x Q)
  // A big Per-day year makes step 2 the slow one, and it used to look like a
  // frozen button.
  const save = async () => {
    if (!functionCode || saving || dirty.size === 0) return;
    setSaving(true);
    setError('');
    setPgFault(null);
    setSavedNote('');
    try {
      const payload = monthsToApi(months, fyStartMonth, financialYear, headsOf, shifts.length)
        .filter((_, idx) => dirty.has(idx));

      setStage(STAGES.plan);
      const res = await labourService.savePlan(moduleCode, functionCode, payload, { deferCalc: true });
      if (!res?.success) {
        setError(res?.message || 'Failed to save the shift plan.');
        return;
      }
      setDirty(new Set());
      const note = `Saved ${res.savedMonths} month${res.savedMonths === 1 ? '' : 's'}`
        + (res.savedCalendar ? ` · ${res.savedCalendar.toLocaleString()} shifts` : '');

      // The plan is committed by now. A calc failure is reported, not fatal —
      // the roster on screen is safe either way.
      setStage(STAGES.calc);
      try {
        const calc = await labourService.recalculate(moduleCode, functionCode);
        if (calc?.success) setSummary(calc.data.summary);
        else setError(`Plan saved, but the cost calculation failed: ${calc?.message || 'unknown error'}`);
      } catch (e) {
        setError(`Plan saved, but the cost calculation failed: ${errText(e, 'unknown error')}`);
        if (e?.response?.data?.pg) setPgFault({ pg: e.response.data.pg, context: 'Save → recalculate' });
      }
      setStage(STAGES.done);
      setSavedNote(note);
    } catch (e) {
      setError(errText(e, 'Failed to save the shift plan.'));
      if (e?.response?.data?.pg) setPgFault({ pg: e.response.data.pg, context: 'Save shift plan' });
    } finally {
      setSaving(false);
      setTimeout(() => setStage(null), 600);
    }
  };

  // Saving the plan recalculates automatically. This is for the other two
  // things that move the number without touching the roster: a change to pay
  // in People, or a change to the overtime rules.
  const recalc = async () => {
    if (!functionCode || recalcing) return;
    setRecalcing(true);
    setError('');
    setPgFault(null);
    try {
      const res = await labourService.recalculate(moduleCode, functionCode);
      if (res?.success) {
        setSummary(res.data.summary);
        // A run that writes nothing is a real outcome, not a no-op: no roster,
        // no crew, or nobody assigned to it. Say so rather than leaving the
        // button looking dead.
        const { hours = 0, costs = 0 } = res.data.rows || {};
        setSavedNote(hours === 0
          ? 'Calculated — but 0 hours. Check that positions in People > Structures are on this function and a crew.'
          : `Calculated · ${hours.toLocaleString()} hour rows, ${costs.toLocaleString()} cost rows`);
      } else {
        setError(res?.message || 'Could not recalculate.');
      }
    } catch (e) {
      setError(errText(e, 'Could not recalculate.'));
      if (e?.response?.data?.pg) setPgFault({ pg: e.response.data.pg, context: 'Recalculate' });
    } finally {
      setRecalcing(false);
    }
  };

  useEffect(() => {
    if (!savedNote) return undefined;
    const t = setTimeout(() => setSavedNote(''), 4000);
    return () => clearTimeout(t);
  }, [savedNote]);

  const markDirty = (from, to = from) => setDirty((prev) => {
    const next = new Set(prev);
    for (let i = from; i <= to; i++) next.add(i);
    return next;
  });
  const applyMonth = (idx, cfg, forward) => {
    setMonths((prev) => prev.map((m, i) => ((forward ? i >= idx : i === idx) ? cloneMonth(cfg) : m)));
    markDirty(idx, forward ? months.length - 1 : idx);
    setOrganising(null);
  };

  // Accept a whole year of proposed rosters. Each entry rebuilds ONE month from
  // the optimiser's pattern plus the rules set in the modal; months the planner
  // did not tick are untouched.
  //
  // Writes to page state and marks those months dirty — it does NOT save. The
  // page's own Save commits them and the real calc then produces the
  // authoritative hours, exactly as if the roster had been drawn by hand.
  const applySchedule = (entries) => {
    if (!entries?.length) return;
    setMonths((prev) => prev.map((m, i) => {
      const e = entries.find((x) => x.periodIdx === i);
      if (!e) return m;
      const { pattern, rules } = e;
      const n = Math.floor(24 / pattern.blockHours);
      const worked = emptyWorked(n);
      const assign = {};
      pattern.blocks.forEach((b) => {
        worked[b.blockIdx] = [...b.worked];
        assign[b.blockIdx] = { ...(m.assign?.[b.blockIdx] || {}), shiftId: b.shiftId };
      });
      const next = {
        ...m,
        blockHours: pattern.blockHours,
        dayStartHour: rules.dayStartHour,
        worked,
        assign,
        dayBlocks: {},
        assignMode: 'timeslot',
        alternating: rules.alternating,
        rotationWeeks: rules.rotationWeeks,
      };
      // Per-day is the same roster shown on real dates, so the search still
      // reasons about a repeating week and the template is painted onto the
      // calendar afterwards. Searching thirty independent days would cost far
      // more and answer the same question.
      if (rules.assignMode === 'perday') {
        return {
          ...next,
          assignMode: 'perday',
          alternating: false,   // rotation is a weekly-template idea
          dayBlocks: materialiseFor(next, calendarFor(i, fyStartMonth, financialYear)),
        };
      }
      return next;
    }));
    const idxs = entries.map((e) => e.periodIdx);
    markDirty(Math.min(...idxs), Math.max(...idxs));
  };

  // Depends on headsOf too: the scheduled total moves when the roster does.
  const totals = useMemo(
    () => ({
      hours: months.reduce((a, m, i) => a + monthHours(m, headsOf, dowCountsFor(i, fyStartMonth, financialYear)), 0),
    }),
    [months, headsOf, fyStartMonth, financialYear],
  );
  const currentFn = functions.find((f) => f.function_code === functionCode);
  const fnOptions = useMemo(
    () => functions.map((f) => ({ code: f.function_code, name: f.function_name })),
    [functions],
  );
  // Costed numbers keyed by crew, so each crew chip can carry its own totals
  // instead of a second strip repeating the same list below.
  const sumByShift = useMemo(() => {
    const m = new Map();
    (summary?.shifts || []).forEach((s) => m.set(Number(s.shift_no), s));
    return m;
  }, [summary]);


  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {title}
          <span className="ml-2 align-middle inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400" title="You plan hours and heads. Cost is calculated against People's encrypted pay and shown as a crew total only — no individual salary is displayed or stored in clear.">Plan hours · costed totals only</span>
        </h1>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Pick a function, set the shifts allowed per month, then click a month to design its shift pattern.
        </p>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {/* Function selector */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5">
          <label htmlFor="labour-function" className="text-xs font-medium text-slate-500 dark:text-slate-400">Function</label>
          {/* Type-to-search rather than a scrolling list: a plant can carry
              dozens of functions, and the code is often what people know. */}
          <SearchSelect
            id="labour-function"
            className="w-64"
            value={functionCode}
            onChange={(code) => code && setFunctionCode(code)}
            options={fnOptions}
            disabled={!ready || functions.length === 0}
            clearable={false}
            placeholder={functions.length === 0 ? '— none yet —' : 'Search functions…'}
            emptyText="No function matches."
            ariaLabel="Function"
          />
          <button type="button" onClick={() => { setEditFn(null); setAddOpen(true); }} disabled={!ready}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40">
            <Plus className="w-3 h-3" /> New
          </button>
          <button type="button" onClick={() => { setEditFn(currentFn); setAddOpen(true); }} disabled={!currentFn}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40"
            title="Overtime multiplier and the cost centre / GL each of basic, overtime and allowance posts to">
            <Pencil className="w-3 h-3" /> Config
          </button>
          <button type="button" onClick={recalc} disabled={!functionCode || recalcing}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40"
            title="Re-run the hours and cost calculation. Saving the plan does this automatically — use it after pay or the overtime rules change.">
            {recalcing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Recalculate
          </button>
          <button type="button" onClick={() => setScheduleOpen(true)} disabled={!functionCode || !ready}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-violet-300 dark:border-violet-500/40 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-500/20 disabled:opacity-40"
            title="Size every month's roster from the volume plan and your hours-per-unit target">
            <Sparkles className="w-3 h-3" /> Optimise
          </button>

          {/* Function totals for the year, from calc_labour_costs. */}
          {functionCode && (
            <div className="ml-auto flex items-center gap-2">
              <Stat label="Hours worked" value={fmtH(summary?.total?.hours)} hint="Clock hours on the roster" />
              <Stat label="Paid equivalent" value={fmtH(summary?.total?.paid_equiv_hours)}
                    hint="After the overtime multipliers"
                    accent={summary?.total?.paid_equiv_hours > summary?.total?.hours} />
              <CostSplit data={summary?.total} />
            </div>
          )}
        </div>

        {/* Recalculate has one server-side step, so there is no honest way to
            say how far along it is — an indeterminate bar, not a fake one. */}
        {recalcing && (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500 shrink-0" />
            <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
              Calculating hours worked, overtime and cost…
            </span>
            <span className="ml-2 h-1 flex-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <span className="block h-full w-1/4 rounded-full bg-emerald-500 animate-indeterminate" />
            </span>
          </div>
        )}

        {!ready && (
          <p className="m-0 text-sm text-slate-500 dark:text-slate-400">Select a budget version and a site to plan shifts.</p>
        )}
        {error && (
          <p className="m-0 text-sm text-rose-600 dark:text-rose-400">{error}</p>
        )}
        {pgFault && (
          <PgErrorPanel pg={pgFault.pg} context={pgFault.context} onDismiss={() => setPgFault(null)} />
        )}

        {ready && functions.length === 0 && !error && (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center">
            <p className="m-0 text-sm text-slate-500 dark:text-slate-400">No functions yet for this module.</p>
            <p className="m-0 mt-1 text-xs text-slate-400">Add one — Picking, Packing, Extrusion — then plan its shifts.</p>
          </div>
        )}

        {/* Crews — name the shifts, and carry each crew's costed totals. The
            calendar still paints S1/S2; these are the names people use, and
            what Structures assigns positions to.

            The numbers live on the chip rather than in a strip of their own:
            a crew and what it costs are the same thought, and one block is a
            lot less page than two. */}
        {ready && functionCode && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-1">Crews</span>
              {shifts.length === 0 && <span className="text-[11px] text-slate-400">None yet — add one to name your shifts.</span>}
              {shifts.map((s) => {
                const st = shiftStyle(s.shift_no);
                const sum = sumByShift.get(Number(s.shift_no));
                return (
                  <span key={s.shift_no} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 pl-2 pr-1 py-1">
                    <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                    <span className="text-[11px] font-bold text-slate-400 w-4">S{s.shift_no}</span>
                    <input value={s.shift_name} onChange={(e) => renameCrew(s.shift_no, e.target.value)}
                      placeholder={`Shift ${s.shift_no}`} aria-label={`Name for shift ${s.shift_no}`}
                      className="w-24 text-xs bg-transparent text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-400 rounded px-1 py-0.5" />
                    {sum ? (
                      <span className="flex flex-col gap-0.5 border-l border-slate-200 dark:border-slate-700 pl-2"
                        title={[
                          `${sum.headcount} head${sum.headcount === 1 ? '' : 's'}`,
                          `${fmtH(sum.hours)} worked → ${fmtH(sum.paid_equiv_hours)} paid equivalent`,
                          `Base ${fmtMoney(sum.base_cost)}`,
                          `Overtime ${fmtMoney(sum.overtime_cost)}  (${fmtH(sum.premium_hours)} premium)`,
                          `Allowance ${fmtMoney(sum.allowance_cost)}`,
                          `Total ${fmtMoney(sum.total_cost)}`,
                        ].join('\n')}>
                        <span className="flex items-center gap-1.5 font-mono text-[11px] leading-none">
                          <span className="text-slate-500 dark:text-slate-400">{fmtH(sum.hours)}</span>
                          <span className={sum.paid_equiv_hours > sum.hours
                            ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}>{fmtH(sum.paid_equiv_hours)}</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-100">{fmtMoney(sum.total_cost)}</span>
                        </span>
                        {/* The crew's own base / overtime / allowance mix. */}
                        {sum.total_cost > 0 && (
                          <span className="flex h-0.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            {COST_SPLITS.map((cs) => (
                              <span key={cs.key} className={cs.dot}
                                style={{ width: `${(Number(sum[cs.key] || 0) / sum.total_cost) * 100}%` }} />
                            ))}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="border-l border-slate-200 dark:border-slate-700 pl-2 text-[11px] text-slate-300 dark:text-slate-600">not costed</span>
                    )}
                    <button type="button" onClick={() => removeCrew(s.shift_no)} title="Remove crew"
                      className="p-0.5 rounded text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-700">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              <button type="button" onClick={addCrew} disabled={shifts.length >= SHIFT_STYLES.length}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40">
                <Plus className="w-3 h-3" /> Crew
              </button>
              <button type="button" onClick={saveCrews} disabled={!shiftsDirty || savingShifts}
                className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md text-white transition-colors ${shiftsDirty && !savingShifts ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600 cursor-default'}`}>
                {savingShifts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save crews
              </button>
            </div>

            {/* What the numbers and the bar on each chip mean, said once. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[10px] text-slate-400">
              <span className="font-mono">hours worked</span>
              <span className="font-mono">paid equivalent</span>
              <span className="font-mono font-semibold">total cost</span>
              <span className="flex items-center gap-2 border-l border-slate-200 dark:border-slate-700 pl-3">
                {COST_SPLITS.map((cs) => (
                  <span key={cs.key} className="inline-flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`} />{cs.label.toLowerCase()}
                  </span>
                ))}
              </span>
              {(summary?.shifts?.length || 0) === 0 && (
                <span className="italic">— save the plan, or Recalculate, to cost this function</span>
              )}
              {currentFn && (currentFn.cost_center_code || currentFn.gl_account_code) && (
                <span className="ml-auto">posts to {currentFn.cost_center_code || '—'} · {currentFn.gl_account_code || '—'}</span>
              )}
            </div>
          </div>
        )}

        {ready && functionCode && (
          <>
            <div className="flex items-center justify-between">
              <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Monthly Shift Plan{loading && <Loader2 className="inline-block w-3.5 h-3.5 ml-2 animate-spin text-slate-400" />}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">Year scheduled: <span className="font-mono font-semibold text-slate-600 dark:text-slate-300">{fmtH(totals.hours)}</span></span>
                {saving || stage ? (
                  <span className="flex flex-col items-end gap-1 min-w-[15rem]">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {stage?.label || 'Saving…'}
                      {stage?.key === 'plan' && pending.months > 0 && (
                        <span className="text-slate-400">
                          {' '}({pending.months} month{pending.months === 1 ? '' : 's'}
                          {pending.rows > 0 && `, ${pending.rows.toLocaleString()} shifts`})
                        </span>
                      )}
                    </span>
                    <span className="h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                      <span className="block h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${stage?.pct || 0}%` }} />
                    </span>
                  </span>
                ) : savedNote ? (
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{savedNote}</span>
                ) : dirty.size > 0 ? (
                  <span className="text-xs text-amber-600 dark:text-amber-400">
                    {pending.months} month{pending.months === 1 ? '' : 's'} unsaved
                  </span>
                ) : null}
                <button onClick={save} disabled={dirty.size === 0 || saving}
                  title={saving ? stage?.label : undefined}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md text-white shadow-sm transition-colors whitespace-nowrap ${dirty.size > 0 && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600 cursor-default'}`}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {saving ? (stage?.key === 'calc' ? 'Calculating' : 'Saving') : 'Save'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {months.map((m, idx) => {
                const usedShifts = distinctShifts(m);
                return (
                  <button key={idx} type="button" onClick={() => setOrganising(idx)}
                    className="text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 hover:border-slate-400 dark:hover:border-slate-400 hover:shadow-sm transition-all">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                        {periodLabel(idx + 1, fyStartMonth)}
                        {dirty.has(idx) && <span title="Unsaved changes" className="inline-block ml-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 align-middle" />}
                      </span>
                      {/* How many shifts may run is now simply how many crews
                          exist — nothing to set per month. */}
                      <span className="text-[10px] text-slate-400" title="Crews available on this function">
                        {shifts.length} crew{shifts.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1 min-h-[1.25rem]">
                      {usedShifts.length === 0 ? <span className="text-[11px] text-slate-400">No shifts set</span>
                        : usedShifts.map((sid) => { const st = shiftStyle(sid); return <span key={sid} title={shiftLabel(sid)} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded ${st.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />S{sid}</span>; })}
                      {m.alternating && <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400">⟳ alt</span>}
                      {m.assignMode === 'perday' && <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-600 dark:bg-slate-600/60 dark:text-slate-300">calendar</span>}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                      <span>
                        {m.blockHours}h
                        {m.dayStartHour ? ` from ${pad(m.dayStartHour)}:00` : ''}
                        {' · '}
                        {m.assignMode === 'perday' ? `${workingDays(m)} days` : `${slotsPerWeek(m)} slots/wk`}
                      </span>
                      <span className="font-mono">{fmtH(monthHours(m, headsOf, dowCountsFor(idx, fyStartMonth, financialYear)))}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {scheduleOpen && (
        <OptimiseScheduleModal
          moduleCode={moduleCode}
          functionCode={functionCode}
          functionName={currentFn?.function_name}
          fyStartMonth={fyStartMonth}
          onApply={applySchedule}
          onClose={() => setScheduleOpen(false)}
        />
      )}

      {organising !== null && (
        <ShiftOrganiser
          month={months[organising]}
          monthIdx={organising}
          fyStartMonth={fyStartMonth}
          financialYear={financialYear}
          shiftLabel={shiftLabel}
          crews={shifts}
          headsOf={headsOf}
          crewName={shiftLabel}
          moduleCode={moduleCode}
          functionCode={functionCode}
          onApply={(cfg) => applyMonth(organising, cfg, false)}
          onApplyForward={(cfg) => applyMonth(organising, cfg, true)}
          onClose={() => setOrganising(null)}
        />
      )}

      {addOpen && (
        <AddLabourFunctionModal
          moduleCode={moduleCode}
          initial={editFn}
          existingCodes={functions.map((f) => f.function_code)}
          costCenters={pickers.costCenters}
          glAccounts={pickers.glAccounts}
          onSaved={(code) => { setAddOpen(false); setEditFn(null); loadFunctions(code); }}
          onClose={() => { setAddOpen(false); setEditFn(null); }}
        />
      )}
    </div>
  );
};

export default VariableLabour;

// A two-option segmented toggle. Used by the optimiser panel to pick the
// volume SOURCE (what the plant makes vs what leaves the site) and the BASIS
// (pallets vs units) — both are one-line choices that change the meaning of
// the rate the planner is about to type, so they sit next to the number.
const Seg = ({ value, onChange, options }) => (
  <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden">
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${value === v
          ? 'bg-violet-600 text-white'
          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
        {label}
      </button>
    ))}
  </div>
);
